'use client';

import { useCallback, useEffect, useState } from 'react';
import { getDb } from '@/lib/db';
import { STORAGE_UNAVAILABLE_MESSAGE, storageErrorMessage } from '@/lib/storageErrors';
import { Transaction } from '@/lib/types';

export type TransactionPayload = Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>;

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await db.transactions.orderBy('date').reverse().toArray();
      setTransactions(list);
      setError(null);
    } catch (err) {
      setError(storageErrorMessage('Impossible de charger les transactions.', err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('budget-db-update', handler);
    return () => window.removeEventListener('budget-db-update', handler);
  }, [load]);

  const addTransaction = useCallback(async (payload: TransactionPayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }
    try {
      const now = new Date().toISOString();
      await db.transactions.add({
        ...payload,
        id: createId(),
        createdAt: now,
        updatedAt: now,
      });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage("Impossible d'ajouter la transaction.", err));
    }
  }, []);

  const updateTransaction = useCallback(async (id: string, payload: TransactionPayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }
    try {
      await db.transactions.update(id, { ...payload, updatedAt: new Date().toISOString() });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de modifier la transaction.', err));
    }
  }, []);

  const deleteTransaction = useCallback(async (id: string) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }
    try {
      await db.transactions.delete(id);
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de supprimer la transaction.', err));
    }
  }, []);

  const resetData = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }
    try {
      await db.transactions.clear();
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de réinitialiser les transactions.', err));
    }
  }, []);

  return {
    transactions,
    loading,
    error,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    resetData,
  };
}
